import { LanguageTranslations, TranslationLeaf } from '../types/Language';

export function isTranslationLeaf(value: unknown): value is string {
  return typeof value === 'string';
}

export function flattenTranslations(
  translations: LanguageTranslations,
  prefix = ''
): TranslationLeaf[] {
  const leaves: TranslationLeaf[] = [];

  for (const [key, value] of Object.entries(translations)) {
    const path = prefix ? `${prefix}.${key}` : key;

    if (isTranslationLeaf(value)) {
      leaves.push({ path, value });
      continue;
    }

    if (value && typeof value === 'object') {
      leaves.push(...flattenTranslations(value, path));
    }
  }

  return leaves.sort((a, b) => a.path.localeCompare(b.path));
}

export function getTranslationAtPath(
  translations: LanguageTranslations,
  path: string
): string | undefined {
  const parts = path.split('.');
  let current: LanguageTranslations | string | undefined = translations;

  for (const part of parts) {
    if (!current || typeof current === 'string') {
      return undefined;
    }
    current = current[part];
  }

  return typeof current === 'string' ? current : undefined;
}

export function setTranslationAtPath(
  translations: LanguageTranslations,
  path: string,
  value: string
): LanguageTranslations {
  const parts = path.split('.');
  const result = structuredClone(translations);
  let current: LanguageTranslations = result;

  for (let index = 0; index < parts.length - 1; index += 1) {
    const part = parts[index];
    const next = current[part];

    if (!next || typeof next === 'string') {
      current[part] = {};
    }

    current = current[part] as LanguageTranslations;
  }

  current[parts[parts.length - 1]] = value;
  return result;
}

export function groupTranslationLeavesBySection(
  leaves: TranslationLeaf[]
): Record<string, TranslationLeaf[]> {
  return leaves.reduce<Record<string, TranslationLeaf[]>>((groups, leaf) => {
    const section = leaf.path.split('.')[0];
    groups[section] = [...(groups[section] ?? []), leaf];
    return groups;
  }, {});
}

const TRANSLATION_PATH_PATTERN = /^[a-zA-Z][a-zA-Z0-9]*(\.[a-zA-Z][a-zA-Z0-9]*)+$/;

export function isValidTranslationPath(path: string): boolean {
  return TRANSLATION_PATH_PATTERN.test(path.trim());
}

export function translationPathExists(
  translations: LanguageTranslations,
  path: string
): boolean {
  const parts = path.split('.');
  let current: LanguageTranslations | string | undefined = translations;

  for (let index = 0; index < parts.length; index += 1) {
    const part = parts[index];

    if (!current || typeof current === 'string') {
      return false;
    }

    if (!(part in current)) {
      return false;
    }

    if (index === parts.length - 1) {
      return true;
    }

    current = current[part];
  }

  return false;
}

export function removeTranslationAtPath(
  translations: LanguageTranslations,
  path: string
): LanguageTranslations {
  const parts = path.split('.');
  const result = structuredClone(translations);

  const removeAt = (current: LanguageTranslations, index: number): boolean => {
    const part = parts[index];

    if (index === parts.length - 1) {
      if (!(part in current)) {
        return false;
      }

      delete current[part];
      return true;
    }

    const next = current[part];
    if (!next || typeof next === 'string') {
      return false;
    }

    const removed = removeAt(next, index + 1);
    if (removed && Object.keys(next).length === 0) {
      delete current[part];
    }

    return removed;
  };

  removeAt(result, 0);
  return result;
}
